// import { Button, Card as NativeCard, Table as NativeTable, Popover, PopoverAnchor, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Text } from "@chakra-ui/react"
// import Icon, { Icons } from "../generic/Icons";

import { Menu, MenuContent, MenuItem, MenuRoot, MenuTrigger, Skeleton, Table as NativeTable, Portal, TableRootProps } from "@chakra-ui/react";
import Text from "./Text";
import Icon, { Icons } from "./Icons";
import { ValidationMessage } from "./hooks/useModel";
import React, { useEffect, useRef, useState } from "react";
import { BASE_FE_PATH, emptyCharacter, SPACING } from "@/generic/config";
import { isMobile } from "react-device-detect";
import { Link } from "react-router-dom";



export type TableProps<R> = {
    columns: Column<R>[];
    rows: R[] | undefined;
    isLoading?: boolean;
    validationMessages?: ValidationMessage<any>[];
    isRowDisabled?: (row: R) => boolean;
    contextMenuActions?: ContextMenuAction<R>[];
    // Either onRowClick or href, not both
} & (
        | { onRowClick: (row: R) => void; href?: never }
        | { href: (row: R) => string; onRowClick?: never }
        | {}
    );

export type Column<R> = {
    scale: number;
    title: string;
    getter: (row: R) => string | number | Date | React.ReactNode;
};

export type ContextMenuAction<R> = {
    title: string;
    onClick: (row: R) => void;
    disabled?: (row: R) => boolean;
};

const Table = <R,>({ tableProps, ...rest }: { tableProps: TableProps<R> } & TableRootProps) => {
    const {
        columns,
        rows,
        isLoading = false,
        isRowDisabled = (r) => false,
        contextMenuActions = [],
    } = tableProps;
    const onRowClick = "onRowClick" in tableProps ? tableProps.onRowClick : undefined;
    const href = "href" in tableProps ? tableProps.href : undefined;

    const hasContextMenu = contextMenuActions.length > 0;
    const scalesSum = columns.filter(c => c.title !== "ID").reduce((acc, column) => acc + column.scale, 0);


    const tableRef = useRef<HTMLTableElement>(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const updateScale = () => {
            if (!tableRef.current) return;

            const tableWidth = tableRef.current.offsetWidth;
            const screenWidth = window.innerWidth;
            setScale(screenWidth < tableWidth ? (screenWidth / tableWidth) * 0.96 : 1); // Reserved for right side
        };

        updateScale();
        window.addEventListener('resize', updateScale);
        window.addEventListener('orientationchange', updateScale);
        window.addEventListener('popstate', updateScale);
        return () => {
            window.removeEventListener('resize', updateScale);
            window.removeEventListener('orientationchange', updateScale);
            window.removeEventListener('popstate', updateScale);
        };
    }, [rows]);

    return (
        <div style={{
            overflowX: 'hidden',
            width: '100%',
        }}>
            <NativeTable.Root ref={tableRef} {...rest} style={{
                width: '100%',
                transformOrigin: 'top left',
                transform: isMobile ? `scale(${scale})` : 'none',
                ...rest.style,
            }}>
                <NativeTable.Header>
                    <NativeTable.Row bg="transparent">
                        {columns.map((column, index) => {
                            const isIdCell = column.title === "ID";
                            const exactWidth = isIdCell ? "2.5%" : `${column.scale / scalesSum * 100}%`;

                            return (
                                <NativeTable.ColumnHeader key={column.title} p={SPACING} py={SPACING} width={exactWidth} maxW={exactWidth} whiteSpace="nowrap" fontSize="xs" fontWeight="600" letterSpacing="0.05em">
                                    {column.title}
                                </NativeTable.ColumnHeader>
                            )
                        })}
                        {hasContextMenu && <NativeTable.ColumnHeader w="3ch" maxW="3ch" />}
                    </NativeTable.Row>
                </NativeTable.Header>
                <NativeTable.Body>
                    {isLoading && Array.from({ length: 5 }).map((_, rowIndex) => (
                        <NativeTable.Row key={`skeleton-${rowIndex}`}>
                            {columns.map((_, colIndex) => (
                                <NativeTable.Cell key={`skeleton-${rowIndex}-${colIndex}`}>
                                    <Skeleton height="1em" width="80%" />
                                </NativeTable.Cell>
                            ))}
                            {hasContextMenu && <NativeTable.Cell />}
                        </NativeTable.Row>
                    ))}
                    {!isLoading && rows?.map((row: any) => {
                        if (!row) return null;

                        return (
                            <NativeTable.Row height="100%" bg="transparent" _hover={onRowClick ?? href ? { bg: "gray.100", _dark: { bg: "gray.700" } } : undefined} cursor={onRowClick ?? href ? "pointer" : "default"} onClick={() => onRowClick ? onRowClick(row) : undefined} key={row.id} textDecor={isRowDisabled(row) ? "line-through" : undefined}>
                                {columns.map((column, index) => {
                                    const value = column.getter(row);
                                    const cell = ({ children }: { children: React.ReactNode }) => (
                                        <NativeTable.Cell key={`${column.title}-${index}`} padding={0} height="100%">
                                            {href && (
                                                <Link to={href ? BASE_FE_PATH + href(row) : ""} style={{ display: "flex", alignItems: "center", minHeight: "100%", padding: SPACING * 4 }}>
                                                    {children}
                                                </Link>
                                            )}
                                            {!href && children}
                                        </NativeTable.Cell>
                                    );
                                    // check if react node
                                    if (React.isValidElement(value)) {
                                        return cell({ children: value });
                                    }
                                    if (value === undefined || value === null) {
                                        return cell({ children: emptyCharacter });
                                    }

                                    const textValue = value instanceof Date ? value.toLocaleDateString() : String(value);

                                    return cell({ children: <Text>{textValue}</Text> });
                                })}
                                {hasContextMenu && (<NativeTable.Cell> <ContextMenuPopover row={row} actions={contextMenuActions} /> </NativeTable.Cell>)}
                            </NativeTable.Row>
                        );
                    })}
                </NativeTable.Body>
            </NativeTable.Root >
        </div>
    )
}

const ContextMenuPopover = <R,>({ row, actions }: { row: R, actions: ContextMenuAction<R>[] }) => {
    return (
        <MenuRoot positioning={{ placement: "left-start" }}>
            <MenuTrigger onClick={event => event.stopPropagation()}>
                <Icon icon={Icons.EllipsisV} p={SPACING} cursor={"pointer"} />
            </MenuTrigger>
            <Portal>
                <Menu.Positioner>
                    <MenuContent tabIndex={-1} w="max-content" onClick={event => event.stopPropagation()}>
                        {actions.map((action, index) => {
                            const isDisabled = action.disabled ? action.disabled(row) : false;
                            const onClick = () => { !isDisabled && action.onClick(row) };
                            const cursor = isDisabled ? "not-allowed" : "pointer";
                            return (
                                <MenuItem key={index} value={action.title} cursor={cursor} disabled={isDisabled} _hover={{ bg: "gray.100" }} onClick={onClick}>{action.title}</MenuItem>
                                // <Text color={color} fontSize="small" fontWeight={weight} key={index} onClick={onClick} cursor={cursor}>{action.title}{isDisabled ? 1 : 0}</Text>
                            )
                        })}
                    </MenuContent>
                </Menu.Positioner>
            </Portal>
        </MenuRoot >
    )
}

export default Table;


